import type { ReactElement, ReactNode } from 'react'
import { DataTableBody } from './DataTableBody.tsx'
import { columnAlignClass } from './table-columns.ts'
import type { Column } from './table-columns.ts'

/**
 * The table, because this app is mostly tables.
 *
 * Dense by construction: 32px rows (`--spacing-row`), 12px cells, a header
 * that sticks so the column names survive a 200-row scroll. The brief's
 * density rule is not a preference — an operator wants a shift's worth of
 * sessions on one screen, and the roomy default table costs two thirds of
 * them.
 *
 * Empty, loading and error are built IN (`DataTableBody`). Every screen
 * here had hand-rolled its own trio and no two agreed; worse, several had
 * no error state at all, so a failed fetch rendered as "no sessions" —
 * which an operator reads as "all clear".
 *
 * Row activation is a real `<button>` in the first cell, never an
 * `onClick` on the `<tr>` alone; see `DataTableBody` for why.
 */

export interface DataTableProps<T> {
  columns: readonly Column<T>[]
  rows: readonly T[]
  /** Stable per-row identity. Required: an index key makes React reuse
   * the wrong row's DOM the moment a poll reorders the list, which on a
   * live sessions table happens every few seconds. */
  getRowKey: (row: T) => string
  /** The table's accessible name, rendered as a visually hidden
   * `<caption>`. Required because these pages carry several tables and
   * "table with 7 columns" four times over is not navigable. */
  caption: string
  isLoading?: boolean
  /** A fetch failure. Takes precedence over loading and empty. */
  error?: string | null
  /** What to show when there are no rows and nothing went wrong. Defaults
   * to a bare `EmptyState`; pass one with an action where there is an
   * obvious next step. */
  empty?: ReactNode
  /** Makes rows activatable. Pair it with `getRowLabel` whenever the
   * first column is not already a readable name. */
  onRowClick?: (row: T) => void
  /** The accessible name of a row's activator button. Without it the
   * button is named by the first cell's text, which is right for a name
   * column and useless for a status chip. */
  getRowLabel?: (row: T) => string
  /** Highlights the row whose detail panel is open. */
  selectedRowKey?: string | null
  /** Sits under the table, outside its scroll container — this is where
   * `Pagination` goes. */
  footer?: ReactNode
}

const HEADER_CLASSES =
  'sticky top-0 z-10 bg-surface px-2 py-1.5 text-xs font-medium text-muted'

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  caption,
  isLoading = false,
  error = null,
  empty,
  onRowClick,
  getRowLabel,
  selectedRowKey = null,
  footer,
}: DataTableProps<T>): ReactElement {
  const hasWidths = columns.some((column) => column.width !== undefined)

  return (
    <div>
      <div className="overflow-x-auto rounded-md border border-border bg-surface">
        <table
          className="w-full border-collapse"
          aria-busy={isLoading || undefined}
        >
          <caption className="sr-only">{caption}</caption>
          {hasWidths && (
            // `<colgroup>` rather than a width on each `<th>`: the header
            // is sticky, and a width set on a sticky cell is ignored by
            // the body cells below it.
            <colgroup>
              {columns.map((column) => (
                <col key={column.key} style={{ width: column.width }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr className="border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`${HEADER_CLASSES} ${columnAlignClass(column.align)}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <DataTableBody
            columns={columns}
            rows={rows}
            getRowKey={getRowKey}
            isLoading={isLoading}
            error={error}
            empty={empty}
            onRowClick={onRowClick}
            getRowLabel={getRowLabel}
            selectedRowKey={selectedRowKey}
          />
        </table>
      </div>
      {footer !== undefined && <div className="mt-2">{footer}</div>}
    </div>
  )
}
